import { EditorView } from 'prosemirror-view'
import { QueryClient, QueryClientProvider } from 'react-query'
import { AIPromptBarPluginKey } from './AIPromptBar'
import { IStackManager } from 'almanac-ui/Modal/Types'
import { PromptBarContainer } from 'common/Prose/nodeViews/AISuggestion/PromptBar/PromptBar'
import { InlineFormatBarPluginKey } from '../InlineFormatBarPlugin/InlineFormatBarPluginKey'
import { AIInlinePromptPluginKey } from '../AIInlinePrompt/AIInlinePrompt'
import { createRoot, Root } from 'react-dom/client'
import { AlmanacEditorState, AlmanacEditorStateContext } from 'stores/EditorState'
import { Provider } from 'react-redux'
import store from 'redux/store'
import { ModalStackContext } from 'almanac-ui/Modal/ModalStack'
import { DocStore, DocStoreContext } from 'stores/DocStore'
import { responsiveDesignLogic } from 'hooks/useResponsiveDesign'
import { WizardType } from 'components/Wizards/schemas/Types'
import { message } from 'antd'

export class AIPromptBarView {
  view: EditorView
  container: HTMLDivElement
  queryClient: QueryClient
  layerId: string
  articleId: string
  channel?: WizardType
  stackManager: IStackManager
  editorStateStore: AlmanacEditorState
  root: Root
  docStore: DocStore

  constructor(view: EditorView) {
    this.view = view
    this.container = document.createElement('div')
    const { queryClient, articleId, layerId, stackManager, docStore, channel } = AIPromptBarPluginKey.getState(
      view.state,
    )
    this.queryClient = queryClient
    this.articleId = articleId
    this.channel = channel
    this.layerId = layerId
    this.stackManager = stackManager
    this.editorStateStore = new AlmanacEditorState(view)
    this.root = createRoot(this.container)
    this.docStore = docStore
  }

  update(view: EditorView) {
    this.view = view
    this.editorStateStore.updateFromView(view)

    const formatBarVisible = InlineFormatBarPluginKey.getState(view.state)?.formatBarVisible
    const inlinePromptActive = AIInlinePromptPluginKey.getState(view.state)?.active
    const hasRevisionNodes = !!this.view.dom.querySelectorAll(`[data-revision-uuid]`).length
    const isStreaming = !!this.view.dom.querySelectorAll(`[data-node-type='ai_suggestion']`).length
    const { isMobile } = responsiveDesignLogic()
    const hidePromptBar = (formatBarVisible && !isMobile) || hasRevisionNodes || inlinePromptActive || isStreaming

    if (!this.view.dom.parentNode) return

    this.view.dom.parentNode.appendChild(this.container)

    this.root.render(
      <Provider store={store}>
        <DocStoreContext.Provider value={this.docStore}>
          <AlmanacEditorStateContext.Provider value={this.editorStateStore}>
            <QueryClientProvider client={this.queryClient}>
              <ModalStackContext.Provider value={this.stackManager}>
                <PromptBarContainer
                  articleId={this.articleId}
                  layerId={this.layerId}
                  view={this.view}
                  anchor={this.container}
                  hidePromptBar={hidePromptBar}
                />
              </ModalStackContext.Provider>
            </QueryClientProvider>
          </AlmanacEditorStateContext.Provider>
        </DocStoreContext.Provider>
      </Provider>,
    )
  }

  destroy() {
    setTimeout(() => this.root.unmount(), 0)
    this.container.remove()
    message.destroy()
  }
}
